
import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { useEffect } from "react";

interface ProtectedRouteProps {
	children?: React.ReactNode;
	requireAdmin?: boolean;
	requireSuperAdmin?: boolean;
}

export const ProtectedRoute = ({ 
	children, 
	requireAdmin = false, 
	requireSuperAdmin = false 
}: ProtectedRouteProps) => {
	const { user, loading, isAdmin, isSuperAdmin } = useAuth();

	useEffect(() => {
		// Log auth state for debugging
		console.log("Protected route auth state:", { 
			isAuthenticated: !!user, 
			isLoading: loading, 
			isAdmin, 
			isSuperAdmin, 
			requiresAdmin: requireAdmin, 
			requiresSuperAdmin: requireSuperAdmin 
		});
	}, [user, loading, isAdmin, isSuperAdmin, requireAdmin, requireSuperAdmin]);

	// While checking authentication state, show a loading spinner
	if (loading) {
		return (
			<div className="flex items-center justify-center h-screen">
				<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-digger-blue"></div>
			</div>
		);
	}

	// If not authenticated, redirect to auth page
	if (!user) {
		console.log("User not authenticated, redirecting to /auth");
		return <Navigate to="/auth" replace />;
	}

	// If route requires super admin access
	if (requireSuperAdmin && !isSuperAdmin) {
		console.log("Access denied: Super admin required");
		return <Navigate to="/" replace />;
	}

	// If route requires admin access
	if (requireAdmin && (!isAdmin && !isSuperAdmin)) {
		console.log("Access denied: Admin required");
		return <Navigate to="/" replace />;
	}

	// If authenticated with proper permissions, show the protected content
	return children ? <>{children}</> : <Outlet />;
};
